import CardActions from '@mui/material/CardActions';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import { GetRule, UpdateRule } from '../../api/rules-endpoint';
import SnackbarAlert from '../../components/Alerts';
import CancelButton from '../../components/CancelButton';
import RuleDetails from '../../components/rules/RuleDetails';
import useConfigStyles from '../../components/useConfigStyles';
import SubmitButton from '../../components/SubmitButton';
import { getLinkToRule } from '../../navigator/links';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRulesContext } from '../../stores/rules-context';
import { Rule } from '../../types/rule';
import { defaultRule } from '../../types/defaultValues';

function RuleEditPage(): JSX.Element {
  const { ruleName } = useParams<{ ruleName: string }>();
  const [rule, setRule] = useState<Rule>(defaultRule);
  const [fetched, setFetched] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [notify, toggleNotification] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const classes = useConfigStyles();
  const navigate = useNavigate();
  const rulesContext = useRulesContext();

  const fetchRule = useCallback(async () => {
    if (ruleName) {
      const response = await GetRule(ruleName);
      if (response.success && response.data) {
        setRule(response.data);
      } else {
        setResponseMessage(response.message || '');
      }
      setFetched(response.success);
    }
  }, [ruleName]);

  useEffect(() => {
    fetchRule();
  }, [fetchRule]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const response = await UpdateRule(ruleName!, rule);
    if (response.success) {
      setResponseMessage('Success');
      rulesContext.fetchData();
      navigate(getLinkToRule(rule.Name));
    } else {
      setResponseMessage(response.message || '');
      setErrors(response.errors || {});
    }
    toggleNotification(true);
  };

  const handleCloseSnackbar = () => {
    toggleNotification(false);
  };

  const handleCancelClick = () => {
    navigate(getLinkToRule(ruleName!));
  };

  const cleanErrors = (name: string) => {
    if (errors[name]) {
      delete errors[name];
    }
  };

  const handleRuleUpdate = (updatedRule: Rule, fieldName: string) => {
    setRule(updatedRule);
    cleanErrors(fieldName);
  };

  return !fetched ? (
    <span>{responseMessage}</span>
  ) : (
    <form onSubmit={handleSubmit} className={classes.form}>
      <Card>
        <CardContent>
          <RuleDetails rule={rule} editable errors={errors} onRuleUpdate={handleRuleUpdate} />
        </CardContent>
        <CardActions style={{'marginBottom': '24px'}}>
          <Grid container alignItems='center' justifyContent='center' direction='row' spacing={5}>
            <Grid item>
              <CancelButton label='Cancel' className={classes.button} onClick={handleCancelClick} />
            </Grid>
            <Grid item>
              <SubmitButton className={classes.button} label='Update Rule' />
            </Grid>
          </Grid>
        </CardActions>
      </Card>
      <SnackbarAlert open={notify} message={responseMessage} handleClose={handleCloseSnackbar} />
    </form>
  );
}

export default RuleEditPage;
